import { Injectable } from "@angular/core";
import { RecordingRepository } from "app/js/src/domain/recording-repository";
import { HttpService } from "../../http/http";
import {
  RecordingJob,
  RecordingJobStatus,
} from "app/js/src/domain/recording-job";
import { Recording } from "app/js/src/domain/recording";
import { RecordingSource } from "app/js/src/domain/recording-source";
import moment from "moment";

@Injectable()
export class RecordingRepositoryImpl implements RecordingRepository {
  private readonly DATEFORMAT = "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]";
  constructor(private httpService: HttpService) {}

  public updateRecordingJob(recordingJob: RecordingJob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const data = this.setRecordingJobRequestMapper(recordingJob);
      this.httpService
        .patch("recording", data)
        .then((result) => {
          if (result) {
            resolve(result);
          } else {
            reject("Failed to update recording job");
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public createRecordingJob(recordingJob: RecordingJob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const data = this.setRecordingJobRequestMapper(recordingJob);
      this.httpService
        .post("recording", data)
        .then((result) => {
          if (result) {
            resolve(recordingJob.id);
          } else {
            reject("failed to create recording job");
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public getRecordingJobs(
    selectTop?: number,
    startDateFrom?: moment.Moment,
    startDateTo?: moment.Moment,
    recordId?: number,
    jobId?: string,
    statuses?: RecordingJobStatus[],
    orderByCreatedTimeDesc?: boolean,
  ): Promise<RecordingJob[]> {
    return new Promise<RecordingJob[]>((resolve, reject) => {
      let url = `recording`;
      const filters = [];
      if (selectTop > 0) {
        filters.push(`SelectTop=${selectTop}`);
      }
      if (startDateFrom) {
        filters.push(`StartDateFrom=${startDateFrom.format("YYYYMMDD")}`);
      }
      if (startDateTo) {
        filters.push(`StartDateTo=${startDateTo.format("YYYYMMDD")}`);
      }
      if (recordId) {
        filters.push(`RecordId=${recordId}`);
      }
      if (statuses) {
        filters.push(`Statuses=${statuses.join("|")}`);
      }
      if (orderByCreatedTimeDesc) {
        filters.push(`OrderByCreatedDayTimeDesc=${orderByCreatedTimeDesc}`);
      }
      if (filters.length > 0) {
        url = url + `?${filters.join("&")}`;
      }
      this.httpService
        .get(url)
        .then((response) =>
          resolve(this.getRecordingJobResponeMapper(response)),
        )
        .catch((err) => {
          reject(err);
        });
    });
  }

  public deleteRecordingJob(id: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.httpService
        .delete(`recording?jobToDelete=${id}`)
        .then((result) => {
          if (result) {
            resolve(true);
          } else {
            reject("failed to delete recording job");
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
  public getRecordingMediaId(
    recordId?: number,
    recordingJobId?: string,
    recordingSourceId?: string,
  ): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      this.httpService
        .post("recording/GetRecordingMediaId", {
          RecordId: recordId,
          RecordingJobId: recordingJobId,
          RecordingSourceId: recordingSourceId,
        })
        .then(resolve)
        .catch(reject);
    });
  }

  private getRecordingJobResponeMapper(response): RecordingJob[] {
    const result = [];
    response.forEach((job) => {
      const sources = [];
      job.Sources.forEach((source) => {
        sources.push(
          new RecordingSource(
            source.Id,
            source.Name,
            source.Type,
            source.MediaUniqueIdTemplate,
            source.ReferenceId,
            false,
            undefined,
            source.StreamingPlatformId,
          ),
        );
      });
      const recordings = [];
      if (job.Records) {
        job.Records.forEach((record) => {
          recordings.push(
            new Recording(
              record.Id,
              record.RecordingJobId,
              record.SourceId,
              moment.utc(record.StartTime),
              record.AssociatedMediaId,
              record.Status,
            ),
          );
        });
      }
      const startTime = moment.utc(job.StartTime);
      result.push(
        new RecordingJob(
          job.Id,
          job.Name,
          startTime,
          startTime.clone().add(job.DurationMs),
          job.Status,
          job.VideoQuality,
          job.AudioQuality,
          sources,
          recordings,
        ),
      );
    });
    return result;
  }
  private setRecordingJobRequestMapper(recordingJob: RecordingJob): any {
    return {
      Type: 0,
      Id: recordingJob.id,
      Name: recordingJob.name,
      StartDate: recordingJob.startTime.format("YYYYMMDD"),
      StartTime: recordingJob.startTime.format("HHmmssSSS"),
      DurationMs: recordingJob.endTime.diff(recordingJob.startTime),
      Status: recordingJob.jobStatus,
      AudioQuality: recordingJob.audioQuality,
      VideoQuality: recordingJob.videoQuality,
      Sources: recordingJob.recordingSources.map((x) => {
        return {
          Id: x.id,
          Name: x.name,
          Type: x.type,
          ReferenceId: x.referenceId,
          MediaUniqueIdTemplate: x.mediaUniqueIdTemplate,
          StreamingPlatformId: x.streamingPlatformId,
        };
      }),
    };
  }
}
